import {
  Badge,
  Box,
  Button,
  Checkbox,
  Group,
  MultiSelect,
  Paper,
  Select,
  Table,
  Text,
  TextInput,
} from "@mantine/core";
import { DatePickerInput } from "@mantine/dates";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

import { StatusBadge } from "./StatusBadge.js";
import type { ServiceProject } from "../hooks/useCustomers.js";
import type { EngagementRef } from "../hooks/useEngagements.js";
import {
  useAssignProjectEngagement,
  useBulkAssignProjectEngagement,
} from "../hooks/useEngagements.js";

/**
 * Projekt-Tabelle im Kundendetail mit Kopfzeilen-Filtern (Titel, Beauftragung,
 * PL (SF), Start-/Ende-Datumsbereich), Mehrfachauswahl (Mark-all wirkt auf die
 * GEFILTERTEN Zeilen) und Sammel-Zuordnung auf eine Beauftragung.
 */

const UNASSIGNED = "__none__";

function formatDate(iso: string | null | undefined): string {
  if (!iso) return "–";
  return new Date(iso).toLocaleDateString("de-DE", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function toIso(d: Date | string | null): string | null {
  if (!d) return null;
  if (typeof d === "string") return d.slice(0, 10);
  // Lokales Datum, kein UTC-Shift (DatePickerInput liefert Mitternacht lokal).
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

type DateRange = [Date | string | null, Date | string | null];

export function ProjectsTable({
  customerId,
  projects,
  engagements,
  canEdit,
}: {
  customerId: string;
  projects: ServiceProject[];
  engagements: EngagementRef[];
  canEdit: boolean;
}) {
  const { t } = useTranslation();
  const assignOne = useAssignProjectEngagement(customerId);
  const assignBulk = useBulkAssignProjectEngagement(customerId);

  const [titleFilter, setTitleFilter] = useState("");
  const [engagementFilter, setEngagementFilter] = useState<string | null>(null);
  const [plFilter, setPlFilter] = useState<string[]>([]);
  const [startRange, setStartRange] = useState<DateRange>([null, null]);
  const [endRange, setEndRange] = useState<DateRange>([null, null]);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkTarget, setBulkTarget] = useState<string | null>(null);

  const engagementNameById = useMemo(
    () => new Map(engagements.map((e) => [e.id, e.name])),
    [engagements],
  );

  // Ein Prädikat für Filterung UND Facetten: die Options-Listen der beiden
  // Auswahl-Filter zeigen nur Werte, die unter den jeweils ANDEREN aktiven
  // Filtern noch vorkommen (Beauftragung gewählt → PLs eingeschränkt, und
  // umgekehrt).
  const matches = useMemo(() => {
    const q = titleFilter.trim().toLowerCase();
    const [sFrom, sTo] = [toIso(startRange[0]), toIso(startRange[1])];
    const [eFrom, eTo] = [toIso(endRange[0]), toIso(endRange[1])];
    return (
      p: ServiceProject,
      opts: { ignorePl?: boolean; ignoreEngagement?: boolean } = {},
    ) => {
      if (q && !`${p.title} ${p.projectNumber}`.toLowerCase().includes(q))
        return false;
      if (!opts.ignoreEngagement) {
        if (engagementFilter === UNASSIGNED && p.engagementId !== null)
          return false;
        if (
          engagementFilter &&
          engagementFilter !== UNASSIGNED &&
          p.engagementId !== engagementFilter
        )
          return false;
      }
      if (
        !opts.ignorePl &&
        plFilter.length > 0 &&
        !plFilter.includes(p.projectLeadSfName ?? "")
      )
        return false;
      if (sFrom && p.startDate < sFrom) return false;
      if (sTo && p.startDate > sTo) return false;
      if (eFrom && p.endDate < eFrom) return false;
      if (eTo && p.endDate > eTo) return false;
      return true;
    };
  }, [titleFilter, engagementFilter, plFilter, startRange, endRange]);

  const plOptions = useMemo(() => {
    const facet = new Set(
      projects
        .filter((p) => matches(p, { ignorePl: true }))
        .map((p) => p.projectLeadSfName)
        .filter(Boolean) as string[],
    );
    // Bereits gewählte Werte behalten, sonst „hängen" sie unsichtbar im Filter.
    for (const v of plFilter) facet.add(v);
    return [...facet].sort().map((name) => ({ value: name, label: name }));
  }, [projects, matches, plFilter]);

  const engagementFilterOptions = useMemo(() => {
    const facetProjects = projects.filter((p) =>
      matches(p, { ignoreEngagement: true }),
    );
    const facetIds = new Set(
      facetProjects.map((p) => p.engagementId).filter(Boolean) as string[],
    );
    if (engagementFilter && engagementFilter !== UNASSIGNED)
      facetIds.add(engagementFilter);
    const opts = engagements
      .filter((e) => facetIds.has(e.id))
      .map((e) => ({ value: e.id, label: e.name }));
    if (
      facetProjects.some((p) => p.engagementId === null) ||
      engagementFilter === UNASSIGNED
    ) {
      opts.unshift({
        value: UNASSIGNED,
        label: t("pages.customers.detail.projectUnassigned"),
      });
    }
    return opts;
  }, [projects, engagements, matches, engagementFilter, t]);

  const filtered = useMemo(
    () => projects.filter((p) => matches(p)),
    [projects, matches],
  );

  const filteredIds = useMemo(() => filtered.map((p) => p.id), [filtered]);
  const allFilteredSelected =
    filteredIds.length > 0 && filteredIds.every((id) => selected.has(id));
  const someFilteredSelected = filteredIds.some((id) => selected.has(id));

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allFilteredSelected) {
        for (const id of filteredIds) next.delete(id);
      } else {
        for (const id of filteredIds) next.add(id);
      }
      return next;
    });
  };

  const toggleOne = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Sammel-Zuordnung nur innerhalb EINER (Kunde × Segment)-Zelle — bei
  // segmentübergreifender Auswahl gezielt warnen statt Server-409.
  const selectedProjects = projects.filter((p) => selected.has(p.id));
  const selectedSegments = new Set(selectedProjects.map((p) => p.segmentId));
  const singleSegment =
    selectedSegments.size === 1 ? [...selectedSegments][0]! : null;
  const bulkTargetOptions = engagements
    .filter((e) => singleSegment !== null && e.segmentId === singleSegment)
    .map((e) => ({ value: e.id, label: e.name }));

  const runBulk = (engagementId: string | null) => {
    assignBulk.mutate(
      { projectIds: [...selected], engagementId },
      {
        onSuccess: () => {
          setSelected(new Set());
          setBulkTarget(null);
        },
      },
    );
  };

  return (
    <>
      {canEdit && selected.size > 0 && (
        <Paper withBorder p="xs" mb="xs">
          <Group gap="sm" wrap="wrap">
            <Text size="sm" fw={600}>
              {t("pages.customers.detail.bulkSelected", {
                count: selected.size,
              })}
            </Text>
            {selectedSegments.size > 1 ? (
              <Text size="sm" c="orange">
                {t("pages.customers.detail.bulkMixedSegments")}
              </Text>
            ) : (
              <>
                <Select
                  size="xs"
                  placeholder={t(
                    "pages.customers.detail.selectEngagementPlaceholder",
                  )}
                  value={bulkTarget}
                  data={bulkTargetOptions}
                  searchable
                  onChange={setBulkTarget}
                  disabled={assignBulk.isPending}
                  style={{ minWidth: 220 }}
                />
                <Button
                  size="compact-xs"
                  disabled={!bulkTarget}
                  loading={assignBulk.isPending}
                  onClick={() => runBulk(bulkTarget)}
                >
                  {t("pages.customers.detail.bulkAssign")}
                </Button>
                <Button
                  size="compact-xs"
                  variant="light"
                  color="orange"
                  loading={assignBulk.isPending}
                  onClick={() => runBulk(null)}
                >
                  {t("pages.customers.detail.bulkUnassign")}
                </Button>
              </>
            )}
            <Button
              size="compact-xs"
              variant="subtle"
              onClick={() => setSelected(new Set())}
            >
              {t("pages.customers.detail.bulkClear")}
            </Button>
          </Group>
        </Paper>
      )}

      {/* miw verhindert gestauchte Spalten (Status-Badge „LÄ…");
          bei schmalem Viewport scrollt die Box. */}
      <Box style={{ overflowX: "auto" }}>
      <Table withTableBorder withColumnBorders miw={900}>
        <Table.Thead>
          <Table.Tr>
            {canEdit && (
              <Table.Th w={36}>
                <Checkbox
                  size="xs"
                  checked={allFilteredSelected}
                  indeterminate={someFilteredSelected && !allFilteredSelected}
                  onChange={toggleAllFiltered}
                  aria-label={t("pages.customers.detail.markAllFiltered")}
                />
              </Table.Th>
            )}
            <Table.Th>{t("pages.customers.detail.tableProjectNumber")}</Table.Th>
            <Table.Th>{t("pages.customers.detail.tableProjectTitle")}</Table.Th>
            <Table.Th>{t("pages.customers.detail.tableProjectEngagement")}</Table.Th>
            <Table.Th>{t("pages.customers.detail.tableProjectLeadSf")}</Table.Th>
            <Table.Th>{t("pages.customers.detail.tableProjectStart")}</Table.Th>
            <Table.Th>{t("pages.customers.detail.tableProjectEnd")}</Table.Th>
            <Table.Th>{t("pages.customers.detail.tableProjectStatus")}</Table.Th>
          </Table.Tr>
          {/* Filterzeile */}
          <Table.Tr>
            {canEdit && <Table.Th />}
            <Table.Th />
            <Table.Th>
              <TextInput
                size="xs"
                placeholder={t("pages.customers.detail.filterTitle")}
                value={titleFilter}
                onChange={(e) => setTitleFilter(e.currentTarget.value)}
              />
            </Table.Th>
            <Table.Th>
              <Select
                size="xs"
                placeholder={t("pages.customers.detail.filterAll")}
                value={engagementFilter}
                data={engagementFilterOptions}
                clearable
                onChange={setEngagementFilter}
              />
            </Table.Th>
            <Table.Th>
              <MultiSelect
                size="xs"
                placeholder={plFilter.length === 0 ? t("pages.customers.detail.filterAll") : undefined}
                value={plFilter}
                data={plOptions}
                clearable
                searchable
                onChange={setPlFilter}
              />
            </Table.Th>
            <Table.Th>
              <DatePickerInput
                type="range"
                size="xs"
                placeholder={t("pages.customers.detail.filterRange")}
                value={startRange as [Date | null, Date | null]}
                onChange={(v) => setStartRange(v as DateRange)}
                clearable
                valueFormat="DD.MM.YY"
              />
            </Table.Th>
            <Table.Th>
              <DatePickerInput
                type="range"
                size="xs"
                placeholder={t("pages.customers.detail.filterRange")}
                value={endRange as [Date | null, Date | null]}
                onChange={(v) => setEndRange(v as DateRange)}
                clearable
                valueFormat="DD.MM.YY"
              />
            </Table.Th>
            <Table.Th />
          </Table.Tr>
        </Table.Thead>
        <Table.Tbody>
          {filtered.map((p) => (
            <Table.Tr key={p.id}>
              {canEdit && (
                <Table.Td>
                  <Checkbox
                    size="xs"
                    checked={selected.has(p.id)}
                    onChange={() => toggleOne(p.id)}
                    aria-label={p.projectNumber}
                  />
                </Table.Td>
              )}
              <Table.Td>
                <Text size="sm" ff="monospace">
                  {p.projectNumber}
                </Text>
              </Table.Td>
              <Table.Td>{p.title}</Table.Td>
              <Table.Td>
                {canEdit ? (
                  <Select
                    size="xs"
                    placeholder={t(
                      "pages.customers.detail.selectEngagementPlaceholder",
                    )}
                    value={p.engagementId}
                    data={engagements
                      .filter((e) => e.segmentId === p.segmentId)
                      .map((e) => ({ value: e.id, label: e.name }))}
                    clearable
                    onChange={(v) =>
                      assignOne.mutate({ projectId: p.id, engagementId: v })
                    }
                    disabled={assignOne.isPending || assignBulk.isPending}
                    style={{ minWidth: 180 }}
                  />
                ) : (
                  <Text size="sm">
                    {p.engagementId
                      ? engagementNameById.get(p.engagementId) ??
                        t("common.dashed")
                      : t("common.dashed")}
                  </Text>
                )}
                {p.engagementId === null && (
                  <Badge color="orange" variant="light" size="xs" mt={2}>
                    {t("pages.customers.detail.projectUnassigned")}
                  </Badge>
                )}
              </Table.Td>
              <Table.Td>{p.projectLeadSfName ?? t("common.dashed")}</Table.Td>
              <Table.Td>{formatDate(p.startDate)}</Table.Td>
              <Table.Td>{formatDate(p.endDate)}</Table.Td>
              <Table.Td>
                <StatusBadge status={p.status} label={p.status} />
              </Table.Td>
            </Table.Tr>
          ))}
          {filtered.length === 0 && (
            <Table.Tr>
              <Table.Td colSpan={canEdit ? 8 : 7}>
                <Text c="dimmed" size="sm">
                  {t("pages.customers.detail.filterNoMatches")}
                </Text>
              </Table.Td>
            </Table.Tr>
          )}
        </Table.Tbody>
      </Table>
      </Box>
    </>
  );
}
